import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from '../hooks/useTranslation';
import { makeTr } from '../services/trLookup';
import {
  getWaitingList,
  saveWaitingListEntry,
  deleteWaitingListEntry,
  getWaitingListQrToken,
} from '../services/serverDataService';

const QR_URL_CACHE_KEY = 'kiosk_notify_qr_url';

export default function NotificationSignupModal({ isOpen, onClose, colors, onCountChange }) {
  const { t, language } = useTranslation();
  const tr = makeTr(language);
  const dark = !!colors?.darkMode;
  const [tab, setTab] = useState('qr'); // 'qr' | 'manual' | 'list'
  const [qrUrl, setQrUrl] = useState(() => {
    try { return localStorage.getItem(QR_URL_CACHE_KEY) || ''; } catch { return ''; }
  });
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auto-Notify Schalter (kiosk_auto_notify)
  const [autoNotify, setAutoNotify] = useState(() => {
    try { return localStorage.getItem('kiosk_auto_notify') === '1'; } catch { return false; }
  });

  // Manuelles Formular
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [strasse, setStrasse] = useState('');
  const [notizen, setNotizen] = useState('');
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState(null);

  // Warteliste
  const [entries, setEntries] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const all = await getWaitingList();
      const waiting = (all || []).filter((e) => e.active !== false && e.status !== 'dauerhaft' && !String(e.firebase_id || '').startsWith('addr_'));
      setEntries(waiting);
      if (onCountChange) {
        const openCount = waiting.filter((e) => !e.benachrichtigt && e.status !== 'benachrichtigt').length;
        onCountChange(openCount);
      }
    } catch { /* ignore offline */ } finally {
      setListLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    if (!isOpen) return;
    loadList();
    setQrLoading(true);
    getWaitingListQrToken()
      .then((res) => {
        if (res && res.url) {
          setQrUrl(res.url);
          try { localStorage.setItem(QR_URL_CACHE_KEY, res.url); } catch { /* */ }
        }
      })
      .catch(() => { /* use cached qrUrl if available */ })
      .finally(() => setQrLoading(false));
  }, [isOpen, loadList]);

  useEffect(() => {
    if (!qrUrl) {
      setQrDataUrl('');
      return;
    }
    QRCode.toDataURL(qrUrl, {
      width: 250,
      margin: 2,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((url) => setQrDataUrl(url))
      .catch(() => setQrDataUrl(''));
  }, [qrUrl]);

  const toggleAutoNotify = () => {
    const next = !autoNotify;
    setAutoNotify(next);
    try { localStorage.setItem('kiosk_auto_notify', next ? '1' : '0'); } catch { /* */ }
  };

  const handleManualSave = async (e) => {
    e.preventDefault();
    setFormMsg(null);
    if (name.trim().length < 2) {
      setFormMsg({ type: 'error', text: tr('Bitte Vor- und Nachnamen eingeben.', 'Please enter first and last name.', 'Voer voor- en achternaam in.') });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFormMsg({ type: 'error', text: tr('Bitte eine gültige E-Mail-Adresse eingeben.', 'Please enter a valid email address.', 'Voer een geldig e-mailadres in.') });
      return;
    }

    let finalStrasse = strasse.trim();
    let finalHausnr = '';
    if (finalStrasse) {
      const m = finalStrasse.match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/);
      if (m) {
        finalStrasse = m[1].trim();
        finalHausnr = m[2].replace(/\s+/g, '');
      }
    }

    setSaving(true);
    try {
      const id = `wait_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      await saveWaitingListEntry(id, {
        name: name.trim(),
        strasse: finalStrasse || null,
        hausnummer: finalHausnr || null,
        kontakt: email.trim().toLowerCase(),
        kontaktTyp: 'email',
        status: 'wartend',
        notizen: notizen.trim() || null,
        active: true,
      });
      setName('');
      setEmail('');
      setStrasse('');
      setNotizen('');
      setFormMsg({
        type: 'success',
        text: tr(
          '✅ Kunde wurde eingetragen! Sobald das Paket gescannt wird, erfolgt die E-Mail-Benachrichtigung.',
          '✅ Customer added! An email notification with pickup PIN will be sent as soon as the parcel is scanned.',
          '✅ Klant toegevoegd! Zodra het pakket wordt gescand, wordt automatisch een e-mail verstuurd.'
        )
      });
      await loadList();
    } catch (err) {
      setFormMsg({ type: 'error', text: err?.message || tr('Fehler beim Speichern.', 'Error saving.', 'Fout bij opslaan.') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    const id = entry.firebase_id || entry.id;
    if (!id) return;
    try {
      await deleteWaitingListEntry(id);
      await loadList();
    } catch { /* */ }
  };

  const handlePrintStandee = () => {
    if (!qrDataUrl) return;
    const win = window.open('', '_blank', 'width=600,height=800');
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>QR Standee - ${language === 'de' ? 'PaketPilot' : 'ParcelPilot'}</title>
        <style>
          body { font-family: system-ui, -apple-system, sans-serif; text-align: center; padding: 36px 24px; color: #0f172a; }
          .card { max-width: 440px; margin: 0 auto; border: 3px solid #2563eb; border-radius: 20px; padding: 28px 24px; }
          h1 { font-size: 26px; margin: 8px 0; }
          p { font-size: 15px; color: #334155; line-height: 1.45; margin: 8px 0; }
          .sub { font-size: 13px; color: #64748b; margin-top: 10px; }
          img { width: 260px; height: 260px; margin: 14px auto; display: block; }
          @media print { .no-print { display: none; } body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="card">
          <div style="font-size: 46px;">📦 🔔</div>
          <h1>${tr('Paket noch nicht da?', 'Parcel not here yet?', 'Pakket nog niet binnen?')}</h1>
          <p><strong>${tr('QR-Code mit dem Handy scannen', 'Scan the QR code with your phone', 'Scan de QR-code met je telefoon')}</strong> ${tr('und Name & E-Mail eintragen.<br/>Sie erhalten automatisch eine E-Mail, sobald Ihr Paket bei uns eintrifft!', 'and enter your name & email.<br/>You will automatically receive an email as soon as your parcel arrives!', 'en vul je naam & e-mail in.<br/>Je ontvangt automatisch een e-mail zodra je pakket er is!')}</p>
          <img src="${qrDataUrl}" alt="QR Code" />
          <div class="sub">
            🇬🇧 <strong>Parcel not here yet?</strong> Scan the QR code to get an automatic email as soon as your parcel arrives.<br/>
            🇩🇪 <strong>Paket noch nicht da?</strong> QR-Code scannen und automatisch per E-Mail informiert werden.<br/>
            🇳🇱 <strong>Pakket nog niet binnen?</strong> Scan de QR-code en ontvang automatisch een e-mail zodra je pakket er is.
          </div>
        </div>
        <div class="no-print" style="margin-top: 20px;">
          <button onclick="window.print()" style="padding: 12px 24px; font-size: 16px; font-weight: bold; background: #2563eb; color: #fff; border: none; border-radius: 10px; cursor: pointer;">
            🖨️ ${tr('Jetzt drucken', 'Print now', 'Nu afdrukken')}
          </button>
        </div>
      </body>
      </html>
    `);
    win.document.close();
  };

  if (!isOpen) return null;

  const openWaitingCount = entries.filter((e) => !e.benachrichtigt && e.status !== 'benachrichtigt').length;

  return (
    <div
      data-testid="notification-signup-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(0,0,0,0.68)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto',
          background: dark ? '#1e2430' : '#ffffff',
          color: dark ? '#f1f5f9' : '#0f172a',
          borderRadius: 16, padding: 20,
          boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 24 }}>🔔</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                {tr('Paket-Benachrichtigung', 'Parcel Notification', 'Pakketmelding')}
              </h3>
              <div style={{ fontSize: 12, color: dark ? '#94a3b8' : '#64748b' }}>
                {tr(
                  'Kunden per E-Mail informieren, sobald ihr Paket gescannt wird',
                  'Notify recipients by email as soon as their parcel is scanned',
                  'Informeer klanten per e-mail zodra hun pakket wordt gescand'
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
              color: dark ? '#94a3b8' : '#64748b', lineHeight: 1, padding: 4
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[
            { id: 'qr', label: tr('📱 QR-Code (Kunde)', '📱 QR Code (Customer)', '📱 QR-code (Klant)') },
            { id: 'manual', label: tr('✍️ Direkt eintragen', '✍️ Add Manually', '✍️ Handmatig toevoegen') },
            { id: 'list', label: `📋 ${tr('Wartend', 'Waiting', 'Wachtend')} (${openWaitingCount})` },
          ].map((tItem) => {
            const active = tab === tItem.id;
            return (
              <button
                key={tItem.id}
                type="button"
                data-testid={`notify-tab-${tItem.id}`}
                onClick={() => { setTab(tItem.id); setFormMsg(null); }}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 12.5, fontWeight: 700,
                  cursor: 'pointer',
                  border: `1.5px solid ${active ? '#3b82f6' : (dark ? '#334155' : '#cbd5e1')}`,
                  background: active ? (dark ? '#1e3a5f' : '#eff6ff') : 'transparent',
                  color: active ? (dark ? '#93c5fd' : '#1d4ed8') : (dark ? '#cbd5e1' : '#475569'),
                }}
              >
                {tItem.label}
              </button>
            );
          })}
        </div>

        {/* TAB 1: QR-CODE FÜR KUNDEN-HANDY */}
        {tab === 'qr' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              padding: '16px 14px', borderRadius: 14,
              background: dark ? '#131822' : '#f8fafc',
              border: `1.5px solid ${dark ? '#334155' : '#e2e8f0'}`,
              marginBottom: 12,
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                {tr('📱 Kunde scannt mit eigenem Smartphone', '📱 Customer scans with their own smartphone', '📱 Klant scant met eigen smartphone')}
              </div>
              <div style={{ fontSize: 12.5, color: dark ? '#94a3b8' : '#64748b', marginBottom: 12, lineHeight: 1.4 }}>
                {tr(
                  'Der Kunde gibt seinen Namen, Straße und E-Mail selbst auf dem Handy ein — du musst nichts abtippen!',
                  'The customer enters their name, street, and email on their own phone — no typing needed!',
                  'De klant vult zelf naam, straat en e-mail in op zijn telefoon — je hoeft niets over te typen!'
                )}
              </div>

              {qrDataUrl ? (
                <div style={{
                  display: 'inline-block', padding: 12, background: '#ffffff',
                  borderRadius: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                }}>
                  <img
                    src={qrDataUrl}
                    alt={t('ui.notify.qrAlt')}
                    data-testid="notify-qr-img"
                    style={{ width: 220, height: 220, display: 'block' }}
                  />
                </div>
              ) : (
                <div style={{ padding: '40px 16px', fontSize: 13, color: dark ? '#94a3b8' : '#64748b' }}>
                  {qrLoading
                    ? tr('⏳ QR-Code wird geladen…', '⏳ Loading QR code…', '⏳ QR-code laden…')
                    : tr('⚠️ Bitte online anmelden, um deinen Shop-QR-Code zu erzeugen.', '⚠️ Please sign in online to generate your shop QR code.', '⚠️ Log online in om je shop-QR-code te maken.')}
                </div>
              )}

              {qrUrl && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handlePrintStandee}
                    style={{
                      padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 12.5
                    }}
                  >
                    🖨️ {tr('Aufsteller drucken', 'Print Standee', 'Standaard afdrukken')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(qrUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2500);
                      } catch { /* */ }
                    }}
                    style={{
                      padding: '9px 14px', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                      background: dark ? '#1e293b' : '#fff',
                      color: dark ? '#e2e8f0' : '#334155', fontWeight: 700, fontSize: 12.5
                    }}
                  >
                    {copied ? tr('✅ Link kopiert!', '✅ Link copied!', '✅ Link gekopieerd!') : tr('🔗 Link kopieren', '🔗 Copy Link', '🔗 Link kopiëren')}
                  </button>
                </div>
              )}
            </div>

            {/* Schalter für vollautomatischen Versand */}
            <div
              onClick={toggleAutoNotify}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '12px 14px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                border: `1.5px solid ${autoNotify ? '#22c55e' : (dark ? '#334155' : '#cbd5e1')}`,
                background: autoNotify ? (dark ? 'rgba(34,197,94,0.12)' : '#f0fdf4') : 'transparent',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {tr('⚡ E-Mail beim Paket-Scan sofort automatisch senden', '⚡ Automatically send email immediately on parcel scan', '⚡ E-mail direct automatisch versturen bij pakketscan')}
                </div>
                <div style={{ fontSize: 11.5, color: dark ? '#94a3b8' : '#64748b', marginTop: 2 }}>
                  {autoNotify
                    ? tr('Aktiv: Sobald du ein passendes Paket speicherst, geht die E-Mail ohne Rückfrage raus.', 'Active: As soon as you save a matching parcel, the email is sent automatically.', 'Actief: Zodra je een passend pakket opslaat, wordt de e-mail direct verstuurd.')
                    : tr('Aus: Beim Speichern eines passenden Pakets fragt die App kurz nach, ob die Mail gesendet werden soll.', 'Off: When saving a matching parcel, the app asks whether to send the email.', 'Uit: Bij het opslaan van een passend pakket vraagt de app of de e-mail moet worden verstuurd.')}
                </div>
              </div>
              <input
                type="checkbox"
                checked={autoNotify}
                onChange={toggleAutoNotify}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 20, height: 20, cursor: 'pointer', flexShrink: 0 }}
              />
            </div>
          </div>
        )}

        {/* TAB 2: DIREKT HIER EINTRAGEN */}
        {tab === 'manual' && (
          <form onSubmit={handleManualSave} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                {tr('Vor- und Nachname *', 'First & Last Name *', 'Voor- en achternaam *')}
              </label>
              <input
                type="text"
                required
                data-testid="notify-manual-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={tr('z. B. Max Mustermann', 'e.g. Alex Smith', 'bijv. Jan Jansen')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                  background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#0f172a'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                {tr('E-Mail-Adresse *', 'Email Address *', 'E-mailadres *')}
              </label>
              <input
                type="email"
                required
                data-testid="notify-manual-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tr('kunde@beispiel.de', 'customer@example.com', 'klant@voorbeeld.nl')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                  background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#0f172a'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                {tr('Straße & Hausnummer (optional)', 'Street & Number / Room (optional)', 'Straat & Huisnummer (optioneel)')}
              </label>
              <input
                type="text"
                data-testid="notify-manual-street"
                value={strasse}
                onChange={(e) => setStrasse(e.target.value)}
                placeholder={tr('z. B. Hauptstraße 12', 'e.g. Main St 12', 'bijv. Hoofdstraat 12')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                  background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#0f172a'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                {tr('Notiz / Sendungsnr. (optional)', 'Note / Order or Tracking No. (optional)', 'Notitie / Zendingnr. (optioneel)')}
              </label>
              <input
                type="text"
                value={notizen}
                onChange={(e) => setNotizen(e.target.value)}
                placeholder={tr('z. B. Express / Retoure', 'e.g. Express / Order #104', 'bijv. Express / Bestelling')}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                  border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                  background: dark ? '#0f172a' : '#fff', color: dark ? '#fff' : '#0f172a'
                }}
              />
            </div>

            {formMsg && (
              <div style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: formMsg.type === 'success' ? (dark ? 'rgba(34,197,94,0.18)' : '#f0fdf4') : (dark ? 'rgba(239,68,68,0.18)' : '#fef2f2'),
                color: formMsg.type === 'success' ? (dark ? '#86efac' : '#166534') : '#ef4444',
                border: `1px solid ${formMsg.type === 'success' ? '#22c55e' : '#ef4444'}`
              }}>
                {formMsg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              data-testid="notify-manual-submit"
              style={{
                marginTop: 4, padding: '12px 16px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff',
                fontWeight: 700, fontSize: 15, cursor: saving ? 'wait' : 'pointer'
              }}
            >
              {saving
                ? tr('⏳ Wird gespeichert…', '⏳ Saving…', '⏳ Opslaan…')
                : tr('🔔 Benachrichtigung speichern', '🔔 Save Notification', '🔔 Melding opslaan')}
            </button>
          </form>
        )}

        {/* TAB 3: WARTELISTE */}
        {tab === 'list' && (
          <div>
            {listLoading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: dark ? '#94a3b8' : '#64748b' }}>
                {tr('⏳ Lade Warteliste…', '⏳ Loading waiting list…', '⏳ Wachtlijst laden…')}
              </div>
            ) : entries.length === 0 ? (
              <div style={{
                padding: 24, textAlign: 'center', fontSize: 13,
                color: dark ? '#94a3b8' : '#64748b',
                border: `1px dashed ${dark ? '#334155' : '#cbd5e1'}`, borderRadius: 12
              }}>
                {tr(
                  'Aktuell warten keine Kunden auf eine Paket-Benachrichtigung.',
                  'No customers are currently waiting for a parcel notification.',
                  'Er wachten momenteel geen klanten op een pakketmelding.'
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {entries.map((e) => {
                  const isNotified = e.benachrichtigt || e.status === 'benachrichtigt';
                  return (
                    <div
                      key={e.firebase_id || e.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        padding: '10px 12px', borderRadius: 10,
                        background: dark ? '#131822' : '#f8fafc',
                        border: `1px solid ${dark ? '#334155' : '#e2e8f0'}`,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{e.name}</span>
                          <span style={{
                            fontSize: 10.5, padding: '2px 7px', borderRadius: 999, fontWeight: 700,
                            background: isNotified ? 'rgba(34,197,94,0.18)' : 'rgba(245,158,11,0.18)',
                            color: isNotified ? '#22c55e' : '#f59e0b'
                          }}>
                            {isNotified ? tr('✅ Benachrichtigt', '✅ Notified', '✅ Gemeld') : tr('⏳ Wartet', '⏳ Waiting', '⏳ Wachtend')}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: dark ? '#94a3b8' : '#64748b', marginTop: 2, wordBreak: 'break-all' }}>
                          ✉️ {e.kontakt}
                          {(e.strasse || e.hausnummer) ? ` · 📍 ${[e.strasse, e.hausnummer].filter(Boolean).join(' ')}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(e)}
                        title={tr('Eintrag entfernen', 'Remove entry', 'Verwijderen')}
                        style={{
                          padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                          background: dark ? '#334155' : '#fee2e2', color: dark ? '#f87171' : '#dc2626',
                          fontSize: 13, fontWeight: 700, flexShrink: 0
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

